/**
 * ChangeRequestBanner — surfaces incoming pending change requests on Home.
 *
 * Per Change Doctrine §"Change-request workflow": the requestee gets
 * notified and can accept / reject / ignore. Ignore + 30 days =
 * auto-accept (handled server-side by core.auto_accept_expired_requests).
 *
 * Rendering:
 *   - Hidden if no incoming pending requests (banner == null).
 *   - One section per request, with: who's affected, which field, the
 *     proposed value vs current, an optional reason, and the auto-accept
 *     deadline. Accept / Reject buttons inline.
 *
 * Aesthetic: quiet left-border accent rule, paper bg. Honors the
 * Manifesto's restraint — this is a request, not an alarm.
 */

import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { utcToLocalDateString } from '@/lib/dates';
import {
  useChangeRequests,
  fieldHumanLabel,
  relativeTime,
  type ChangeRequestRow,
} from '@/hooks/useChangeRequests';

export function ChangeRequestBanner() {
  const { contact } = useAuth();
  const { incoming, refresh } = useChangeRequests();
  const [respondingId, setRespondingId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  if (!contact || incoming.length === 0) return null;

  const tz = contact.timezone || 'UTC';

  async function handleResponse(req: ChangeRequestRow, action: 'accept' | 'reject') {
    setRespondingId(req.id);
    setErrorMessage(null);
    try {
      const { error } = await supabase.schema('core').rpc('respond_to_date_change', {
        p_request_id: req.id,
        p_action: action,
      });
      if (error) throw new Error(error.message);
      refresh();
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Could not respond.');
    } finally {
      setRespondingId(null);
    }
  }

  return (
    <section
      className="border-l-2 border-accent pl-6 py-4 my-10 text-left max-w-xl mx-auto"
      aria-label="Pending change requests"
    >
      <p className="text-[0.7rem] tracking-[0.3em] uppercase text-ink/55 mb-4">
        {incoming.length} change request{incoming.length === 1 ? '' : 's'} await
        {incoming.length === 1 ? 's' : ''} you
      </p>

      <ul className="space-y-7 list-none pl-0">
        {incoming.map((req) => (
          <li key={req.id} className="space-y-2">
            <p className="text-sm text-ink/80 font-display italic leading-relaxed">
              An Orchist proposes that <span className="not-italic">{fieldHumanLabel(req.field)}</span> become{' '}
              <strong className="not-italic text-ink">
                {utcToLocalDateString(req.proposedValue, tz) || '—'}
              </strong>
              .
            </p>

            {req.reason && (
              <p className="text-xs text-ink/55 italic font-display">
                "{req.reason}"
              </p>
            )}

            <p className="text-xs text-ink/45 italic font-display">
              Currently:{' '}
              <span className="not-italic">
                {utcToLocalDateString(req.currentValue, tz) || '(unset)'}
              </span>
              . The cosmos will accept this change {relativeTime(req.expiresAt)} if no response.
            </p>

            <div className="flex gap-3 pt-2 text-xs">
              <button
                type="button"
                onClick={() => handleResponse(req, 'accept')}
                disabled={respondingId === req.id}
                className="px-4 py-2 bg-ink text-paper font-display tracking-wide hover:bg-ink/85 transition-colors disabled:opacity-50"
              >
                {respondingId === req.id ? 'Working...' : 'Accept'}
              </button>
              <button
                type="button"
                onClick={() => handleResponse(req, 'reject')}
                disabled={respondingId === req.id}
                className="px-4 py-2 border border-ink/30 text-ink/70 font-display italic tracking-wide hover:border-ink/60 hover:text-ink transition-colors disabled:opacity-50"
              >
                Reject
              </button>
            </div>
          </li>
        ))}
      </ul>

      {errorMessage && (
        <p className="text-xs text-destructive italic mt-4" role="alert">
          {errorMessage}
        </p>
      )}
    </section>
  );
}
